import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BarChart3, Download, RefreshCcw, Lock, TrendingUp, MapPin, Package, FileText, Link2, Calendar, Video } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { logWarn } from '../lib/logger';
import SEO from '../components/SEO';
import { CharlieTranscripts } from '../components/admin/CharlieTranscripts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'ctrl-analytics-admin-key';
const keyStore = () => window.sessionStorage;

const EVENT_LABELS = {
  request_demo_click: 'Request Demo click',
  request_quote_click: 'Request Quote click',
  gated_pdf_unlock: 'Gated PDF unlock',
  warranty_register_open: 'Warranty registration open',
  video_unmute: 'Hero video unmute',
  video_watch_full: 'Full case-study video open',
  partner_contact_click: 'Partner contact click',
  part_quote_click: 'Kit part quote click',
  chat_open: 'Charlie Echo chat opened',
  chat_message: 'Charlie Echo question asked',
  chat_lead_submit: 'Charlie Echo lead captured',
};

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-5 flex items-start gap-4 shadow-sm">
    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 text-white flex items-center justify-center flex-shrink-0">
      <Icon className="w-5 h-5" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{label}</p>
      <p className="text-2xl font-bold text-[#1e2d3b] mt-1 leading-tight">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  </div>
);

const RankRow = ({ rank, label, count, total }) => {
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <div className="py-2.5 border-b border-slate-100 last:border-b-0">
      <div className="flex items-center gap-3 mb-1">
        <span className="text-xs font-semibold text-slate-400 w-5">#{rank}</span>
        <span className="text-sm text-slate-800 truncate flex-1" title={label}>{label}</span>
        <span className="text-sm font-semibold text-[#1e2d3b] tabular-nums">{count}</span>
        <span className="text-xs text-slate-400 w-10 text-right tabular-nums">{pct}%</span>
      </div>
      <div className="ml-8 h-1.5 bg-slate-100 rounded overflow-hidden">
        <div className="h-full bg-gradient-to-r from-blue-500 to-orange-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const SectionCard = ({ icon: Icon, title, subtitle, rows, total, empty }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm h-full">
    <div className="flex items-center gap-2 mb-1">
      {Icon && <Icon className="w-4 h-4 text-slate-500" />}
      <h3 className="text-sm font-bold text-[#1e2d3b] uppercase tracking-wide">{title}</h3>
    </div>
    {subtitle && <p className="text-xs text-slate-500 mb-3">{subtitle}</p>}
    <div className="mt-2">
      {rows && rows.length > 0 ? (
        rows.map((row, i) => (
          <RankRow key={row.label ?? i} rank={i + 1} label={row.label} count={row.count} total={total} />
        ))
      ) : (
        <p className="text-sm text-slate-400 py-8 text-center italic">{empty || 'No data yet.'}</p>
      )}
    </div>
  </div>
);

const AdminAnalyticsPage = () => {
  const [adminKey, setAdminKey] = useState('');
  const [keyInput, setKeyInput] = useState('');
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    try {
      const saved = keyStore().getItem(STORAGE_KEY);
      if (saved) setAdminKey(saved);
    } catch (e) { logWarn('Admin key restore skipped', e); }
  }, []);

  const fetchAll = useCallback(async () => {
    if (!adminKey) return;
    setLoading(true);
    setError('');
    try {
      const [s, r] = await Promise.all([
        axios.get(`${API}/events/summary`, { params: { admin_key: adminKey, days } }),
        axios.get(`${API}/events/recent`, { params: { admin_key: adminKey, limit: 50 } }),
      ]);
      setSummary(s.data);
      setRecent(r.data.events || []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        setError('Invalid admin key. Please check the ANALYTICS_ADMIN_KEY value in backend/.env.');
        setAdminKey('');
        try { keyStore().removeItem(STORAGE_KEY); } catch (e) { logWarn('Admin key clear failed', e); }
      } else {
        setError(err?.response?.data?.detail || err.message || 'Failed to load analytics.');
      }
    } finally {
      setLoading(false);
    }
  }, [adminKey, days]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleAuth = (e) => {
    e.preventDefault();
    const k = keyInput.trim();
    if (!k) return;
    setAdminKey(k);
    try { keyStore().setItem(STORAGE_KEY, k); } catch (e) { logWarn('Admin key persist failed', e); }
  };

  const handleLogout = () => {
    setAdminKey('');
    setKeyInput('');
    try { keyStore().removeItem(STORAGE_KEY); } catch (e) { logWarn('Admin key clear failed', e); }
    setSummary(null);
    setRecent([]);
  };

  const exportCsv = () => {
    if (!recent.length) return;
    const cols = ['created_at', 'event_name', 'page_path', 'product', 'resource', 'utm_source', 'utm_medium', 'utm_campaign', 'referrer'];
    const header = cols.join(',');
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = recent.map((e) => cols.map((c) => esc(e[c])).join(','));
    const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ctrl-cta-events-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!adminKey) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <SEO title="Analytics — ctrl.aero" description="CTA analytics dashboard for CTRL Systems." noIndex />
        <div className="w-full max-w-sm bg-white rounded-2xl border border-slate-200 p-8 shadow-lg">
          <div className="flex items-center gap-2 mb-2">
            <Lock className="w-5 h-5 text-blue-600" />
            <h1 className="text-xl font-bold text-[#1e2d3b]">CTA Analytics</h1>
          </div>
          <p className="text-sm text-slate-600 mb-6">Enter the admin key to view dashboard.</p>
          <form onSubmit={handleAuth} className="space-y-4">
            <div>
              <Label htmlFor="admin-key" className="text-sm">Admin key</Label>
              <Input
                id="admin-key"
                type="password"
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                placeholder="ANALYTICS_ADMIN_KEY"
                autoFocus
                data-testid="analytics-admin-key-input"
                className="mt-1"
              />
            </div>
            {error && <p className="text-sm text-red-600" data-testid="analytics-admin-error">{error}</p>}
            <Button type="submit" data-testid="analytics-admin-submit" className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white">
              Unlock
            </Button>
          </form>
          <p className="text-xs text-slate-400 mt-6 leading-relaxed">
            The key is set as <code className="bg-slate-100 px-1 rounded">ANALYTICS_ADMIN_KEY</code> in <code className="bg-slate-100 px-1 rounded">backend/.env</code>.
          </p>
        </div>
      </div>
    );
  }

  const total = summary?.total_events || 0;
  const eventRows = (summary?.by_event || []).map((r) => ({ label: EVENT_LABELS[r.event_name] || r.event_name, count: r.count }));
  const pageRows = (summary?.by_page || []).map((r) => ({ label: r.page_path, count: r.count }));
  const productRows = (summary?.by_product || []).map((r) => ({ label: r.product, count: r.count }));
  const utmSourceRows = (summary?.by_utm_source || []).map((r) => ({ label: r.utm_source, count: r.count }));
  const utmCampaignRows = (summary?.by_utm_campaign || []).map((r) => ({ label: r.utm_campaign, count: r.count }));
  const resourceRows = (summary?.by_resource || []).map((r) => ({ label: r.resource, count: r.count }));
  const partnerRows = (summary?.by_partner || []).map((r) => ({ label: r.resource, count: r.count }));
  const partnerTotal = partnerRows.reduce((n, r) => n + r.count, 0);

  const eventTotal = eventRows.reduce((a, b) => a + b.count, 0);
  const cta = eventRows.find((r) => r.label.includes('Demo'))?.count || 0;
  const quote = eventRows.find((r) => r.label.includes('Quote'))?.count || 0;
  const pdf = eventRows.find((r) => r.label.includes('PDF'))?.count || 0;
  const byName = Object.fromEntries((summary?.by_event || []).map((r) => [r.event_name, r.count]));
  const videoUnmute = byName.video_unmute || 0;
  const videoFull = byName.video_watch_full || 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO title="Analytics — ctrl.aero" description="CTA analytics dashboard for CTRL Systems." noIndex />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#1e2d3b] flex items-center gap-2">
              <BarChart3 className="w-7 h-7 text-blue-600" />
              CTA Analytics
            </h1>
            <p className="text-sm text-slate-500 mt-1">Sales-qualified interest signals, real-time from MongoDB.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              data-testid="analytics-range-select"
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
            >
              <option value={1}>Last 24h</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last 12 months</option>
            </select>
            <Button variant="outline" onClick={fetchAll} disabled={loading} data-testid="analytics-refresh-btn">
              <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button variant="outline" onClick={exportCsv} disabled={!recent.length} data-testid="analytics-export-csv-btn">
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
            <Button variant="outline" onClick={handleLogout} data-testid="analytics-logout-btn">Sign out</Button>
          </div>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6 text-sm" data-testid="analytics-error-banner">{error}</div>}

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8" data-testid="analytics-stat-grid">
          <StatCard icon={TrendingUp} label="Total events" value={total.toLocaleString()} sub={`Last ${days} days`} />
          <StatCard icon={Calendar} label="Demo clicks" value={cta.toLocaleString()} sub={total ? `${Math.round((cta / total) * 100)}% of total` : '—'} />
          <StatCard icon={Package} label="Quote clicks" value={quote.toLocaleString()} sub={total ? `${Math.round((quote / total) * 100)}% of total` : '—'} />
          <StatCard icon={FileText} label="PDF unlocks" value={pdf.toLocaleString()} sub={total ? `${Math.round((pdf / total) * 100)}% of total` : '—'} />
          <StatCard icon={Video} label="Video engagement" value={(videoUnmute + videoFull).toLocaleString()} sub={`${videoUnmute} unmutes · ${videoFull} full-video opens`} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
          <SectionCard icon={BarChart3} title="Events by type" subtitle={`Total: ${eventTotal}`} rows={eventRows} total={eventTotal} />
          <SectionCard icon={MapPin} title="Top pages" subtitle="Where the CTA click happened" rows={pageRows} total={total} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
          <SectionCard icon={Package} title="Top products" subtitle="Attributed via page context" rows={productRows} total={total} empty="No product context yet." />
          <SectionCard icon={Link2} title="Top UTM sources" rows={utmSourceRows} total={total} empty="No UTM-tagged visits yet." />
          <SectionCard icon={Link2} title="Top UTM campaigns" rows={utmCampaignRows} total={total} empty="No UTM-tagged visits yet." />
        </div>

        <div className="grid grid-cols-1 gap-4 mb-8">
          <SectionCard icon={FileText} title="Top gated PDF unlocks" subtitle="Which resources drive the most leads" rows={resourceRows} total={pdf} empty="No PDF downloads yet." />
          <SectionCard icon={Package} title="Partner interest" subtitle="Clicks on partner emails, phones and websites" rows={partnerRows} total={partnerTotal} empty="No partner contact clicks yet." />
        </div>

        <CharlieTranscripts adminKey={adminKey} days={days} />

        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-[#1e2d3b] uppercase tracking-wide">Recent events (latest 50)</h3>
            {loading && <span className="text-xs text-slate-400">Loading…</span>}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="analytics-recent-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-4">Time</th>
                  <th className="py-2 pr-4">Event</th>
                  <th className="py-2 pr-4">Page</th>
                  <th className="py-2 pr-4">Product</th>
                  <th className="py-2 pr-4">Resource</th>
                  <th className="py-2 pr-4">UTM source</th>
                  <th className="py-2 pr-4">Campaign</th>
                </tr>
              </thead>
              <tbody>
                {recent.length === 0 && (
                  <tr><td colSpan={7} className="py-8 text-center text-slate-400 italic">No events yet.</td></tr>
                )}
                {recent.map((e) => (
                  <tr key={e.id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50" data-testid="analytics-recent-row">
                    <td className="py-2 pr-4 whitespace-nowrap text-slate-500 tabular-nums text-xs">{(e.created_at || '').slice(0, 19).replace('T', ' ')}</td>
                    <td className="py-2 pr-4 font-medium text-[#1e2d3b]">{EVENT_LABELS[e.event_name] || e.event_name}</td>
                    <td className="py-2 pr-4 text-slate-700 max-w-[220px] truncate" title={e.page_path}>{e.page_path}</td>
                    <td className="py-2 pr-4 text-slate-700">{e.product || '—'}</td>
                    <td className="py-2 pr-4 text-slate-700 max-w-[220px] truncate" title={e.resource || ''}>{e.resource || '—'}</td>
                    <td className="py-2 pr-4 text-slate-700">{e.utm_source || '—'}</td>
                    <td className="py-2 pr-4 text-slate-700">{e.utm_campaign || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalyticsPage;
